import { i18n } from '@/shared/i18n';
import type { SortDirection } from '@/shared/ui/data-table';

import type { ToolsTableV2RowData } from './ToolsTableV2.types';

export type ToolSortKey = Extract<keyof ToolsTableV2RowData, 'updatedAt' | 'displayName'>;

/**
 * `null` is the reset row, most recently modified first, so it is not listed among the choices.
 * See {@link getToolSortConfig} for the order without the builder.
 */
export const TOOL_SORT_CHOICES = [
  {
    id: 'updatedAt-asc',
    get label() {
      return i18n.t('admin:admin.tools.lastModifiedOldestFirst');
    },
    key: 'updatedAt',
    direction: 'asc',
  },
  {
    id: 'displayName-asc',
    get label() {
      return i18n.t('admin:admin.tools.nameAZ');
    },
    key: 'displayName',
    direction: 'asc',
  },
  {
    id: 'displayName-desc',
    get label() {
      return i18n.t('admin:admin.tools.nameZA');
    },
    key: 'displayName',
    direction: 'desc',
  },
] as const satisfies ReadonlyArray<{
  id: string;
  label: string;
  key: ToolSortKey;
  direction: SortDirection;
}>;

export type ToolSortId = (typeof TOOL_SORT_CHOICES)[number]['id'];

// eslint-disable-next-line i18next/no-literal-string -- sort key and direction, not copy
const NEWEST_FIRST = { key: 'updatedAt', direction: 'desc' } as const;
// eslint-disable-next-line i18next/no-literal-string -- sort key and direction, not copy
const A_TO_Z = { key: 'displayName', direction: 'asc' } as const;

interface ToolSort {
  key: ToolSortKey;
  direction: SortDirection;
  label: string;
}

/**
 * Only built agents carry a modification date, so without the builder no row has one and a date
 * order would be plain A to Z. A to Z then becomes the reset row, which leaves Z to A as the only
 * choice.
 */
export function getToolSortConfig(showLastModified: boolean) {
  const choices = showLastModified
    ? TOOL_SORT_CHOICES
    : TOOL_SORT_CHOICES.filter((choice) => choice.id === 'displayName-desc');
  const reset = showLastModified
    ? { ...NEWEST_FIRST, label: i18n.t('admin:admin.tools.lastModifiedNewestFirst') }
    : { ...A_TO_Z, label: i18n.t('admin:admin.tools.nameAZ') };

  return {
    choices,
    reset,
    /** An order the choices do not list is the reset order, which the chip holds as `null`. */
    toSortId: (id: string | null): ToolSortId | null =>
      choices.find((choice) => choice.id === id)?.id ?? null,
    resolve: (id: ToolSortId | null): ToolSort =>
      choices.find((choice) => choice.id === id) ?? reset,
  };
}
